import React, { useState } from "react";
import { Paper } from "@mui/material";
import { format } from "date-fns";

import {ANLoadingScreen } from "@answersnow/pkg-theme";
import useClinicianGetHours from "hooks/api/clinician/useClinicianGetHours";
import { getCancellationsNoShowsDates, FILTERS } from "../utils";
import { ModuleContainer } from "../styles";
import CancellationsNoShowsModule from "./CancellationsNoShowsModule";
/** @typedef {import("api/typesDef").Client} Client */

/**
 * @component
 * @description `CancellationsNoShows` component to display cancellations and no-shows hours by client and period (weeks or months) in a bar chart.
 * @param {Object} props
 * @param {Client[]} props.clients - The list of clients assigned to the clinician
 * @param {string} props.clinicianId - The clinician id
 * @param {boolean} props.isSmallScreen - Whether the screen is small
 * @returns {JSX.Element} The CancellationsNoShows component
 */
const CancellationsNoShows = ({ clients, clinicianId, isSmallScreen }) => {
  const [period, setPeriod] = useState(FILTERS.BY_WEEKS);
  /** @type {[Client, (client: Client) => void]} */
  const [selectedClient, setSelectedClient] = useState("all");
  const { startDate, endDate } = getCancellationsNoShowsDates(period);

  // Request hours from the API
  const { hours, isHoursLoading } = useClinicianGetHours(clinicianId, {
    scheduled: true,
    cancellations: true,
    noShows: true,
    startDate: format(startDate, "MM/dd/yyyy"),
    endDate: format(endDate, "MM/dd/yyyy"),
    period: period === FILTERS.BY_WEEKS ? "1 week" : "1 month",
    ...(selectedClient !== "all" && { clientId: selectedClient.client_id }),
  });

  if (isHoursLoading) {
    return (
      <Paper
        component="section"
        elevation={2}
        sx={ModuleContainer}
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          justifyContent: "center",
        }}
      >
        <ANLoadingScreen height="100%" />
      </Paper>
    );
  }

  return (
    <CancellationsNoShowsModule
      dataHours={hours}
      clients={clients}
      period={period}
      onPeriodChange={setPeriod}
      selectedClient={selectedClient}
      onClientChange={setSelectedClient}
      isSmallScreen={isSmallScreen}
    />
  );
};

export default CancellationsNoShows;
